import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, AlertTriangle, Clock, ShoppingCart, AlertCircle, RefreshCw, Calendar } from 'lucide-react';

// Datos simulados como fallback (si la API falla) - ajustados por tipo y fecha, con claves exactas de API (minúsculas)
const mockDashboardData = (idCliente, tipo, fecha) => {
  const baseData = {
    montoCompras: 12500.50,
    montoDevoluciones: 850.75,
    pedidosCurso: 12,
    transaccionesRealizadas: 128,
  };

  // Simula variación basada en tipo y fecha (solo para demo)
  let multiplier = 1;
  if (tipo === 'mes') {
    const [ano, mes] = fecha.split('-').map(Number);
    multiplier = (mes / 6); // Meses más altos tienen más actividad
    baseData.montoCompras *= multiplier;
    baseData.transaccionesRealizadas *= multiplier;
    baseData.pedidosCurso *= multiplier; // Ajusta pedidos en curso
  } else if (tipo === 'año') {
    const ano = parseInt(fecha);
    multiplier = (ano / 2020); // Años más recientes tienen más actividad
    baseData.montoCompras *= multiplier;
    baseData.transaccionesRealizadas *= multiplier;
    baseData.pedidosCurso *= multiplier; // Ajusta pedidos en curso
  }

  return baseData;
};

const Dashboard = ({ currentUser, token }) => {
  const [dashboardData, setDashboardData] = useState({
    montoCompras: 0,
    montoDevoluciones: 0,
    pedidosCurso: 0,
    transaccionesRealizadas: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);
  const [filterType, setFilterType] = useState('mes'); // 'mes' o 'año'
  const [filterValue, setFilterValue] = useState({
    mes: new Date().getMonth() + 1, // Mes actual (1-12)
    ano: new Date().getFullYear(), // Año actual
  });
  const [applyingFilter, setApplyingFilter] = useState(false);

  // URL de tu API para el dashboard (¡CÁMBIALA por la tuya!)
  const API_DASHBOARD_URL = 'https://appwebtienda-a6b3ecgnggbjh6c7.centralus-01.azurewebsites.net/api/ventas/dashboard'; // Endpoint que devuelve todos los indicadores en un objeto

  const fetchDashboardData = async (type = filterType, value = filterValue) => {
    if (!currentUser || !currentUser.idCliente || !token) {
      setError('No se pudo cargar el dashboard. Verifica tu sesión.');
      setLoading(false);
      loadFallbackData(type, value);
      return;
    }

    setLoading(true);
    setError('');
    setUsingFallback(false);

    try {
      // Construye fecha según tipo
      let fecha;
      if (type === 'mes') {
        fecha = `${value.ano}-${String(value.mes).padStart(2, '0')}`; // e.g., "2024-05"
      } else if (type === 'año') {
        fecha = value.ano.toString(); // e.g., "2024"
      }

      const queryParams = `idCliente=${currentUser.idCliente}&tipo=${type}&fecha=${fecha}`;

      console.log('Llamando API con params:', queryParams); // Debug: ver params en consola
      console.log('Token usado (desencriptado):', token.substring(0, 20) + '...'); // Debug: parte del token (sin exponer completo)

      const response = await fetch(`${API_DASHBOARD_URL}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`, // Token DESENCRIPTADO (plano) en el header
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status); // Debug: status de respuesta

      if (response.ok) {
        const data = await response.json();
        console.log('Data recibida:', data); // Debug: ver respuesta completa
        if (data && data.response) { // Asume estructura { response: { ...datos } }
          setDashboardData({
            montoCompras: parseFloat(data.response.montoCompras) || 0, // Clave exacta de API (minúscula)
            montoDevoluciones: parseFloat(data.response.montoDevoluciones) || 0, // Clave exacta de API (minúscula)
            pedidosCurso: parseInt(data.response.pedidosCurso) || 0, // Clave exacta de API (minúscula)
            transaccionesRealizadas: parseInt(data.response.transaccionesRealizadas) || 0, // Clave exacta de API (minúscula)
          });
        } else {
          throw new Error('Respuesta inválida de la API');
        }
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error al cargar dashboard:', err);
      setError(`No se pudieron cargar los indicadores: ${err.message}. Usando datos de ejemplo.`);
      setUsingFallback(true);
      loadFallbackData(type, value);
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = (type, value) => {
    if (currentUser && currentUser.idCliente) {
      // Para fallback, construye fecha igual que en fetch
      let fecha;
      if (type === 'mes') {
        fecha = `${value.ano}-${String(value.mes).padStart(2, '0')}`;
      } else {
        fecha = value.ano.toString();
      }
      const mockData = mockDashboardData(currentUser.idCliente, type, fecha);
      setDashboardData(mockData);
    }
  };

  const handleFilterChange = (newType) => {
    setFilterType(newType);
    // Resetear valor si cambia tipo
    if (newType === 'mes') {
      setFilterValue({
        mes: new Date().getMonth() + 1,
        ano: new Date().getFullYear(),
      });
    } else {
      setFilterValue({
        mes: null,
        ano: new Date().getFullYear(),
      });
    }
    // Aplicar inmediatamente con el nuevo tipo y valor por defecto
    fetchDashboardData(newType, newType === 'mes' ? { mes: new Date().getMonth() + 1, ano: new Date().getFullYear() } : { ano: new Date().getFullYear() });
  };

  const handleMonthChange = (e) => {
    const [ano, mes] = e.target.value.split('-').map(Number); // input month da 'YYYY-MM'
    setFilterValue({ mes, ano });
    fetchDashboardData('mes', { mes, ano });
  };

  const handleYearChange = (e) => {
    const ano = parseInt(e.target.value);
    setFilterValue({ ano });
    fetchDashboardData('año', { ano });
  };

  useEffect(() => {
    fetchDashboardData(); // Carga inicial con mes actual
  }, [currentUser, token]); // Recarga si cambian user o token

  const handleRetry = () => {
    setError('');
    fetchDashboardData(filterType, filterValue);
  };

  const indicators = [
    {
      title: 'Monto de Compras',
      value: `PEN ${dashboardData.montoCompras.toFixed(2)}`, // Clave minúscula
      icon: DollarSign,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-100',
      description: 'Total de ventas',
    },
    {
      title: 'Monto de Devoluciones',
      value: `PEN ${dashboardData.montoDevoluciones.toFixed(2)}`, // Clave minúscula
      icon: AlertTriangle,
      color: 'from-red-500 to-pink-600',
      bgColor: 'bg-red-100',
      description: 'Total de reembolsos',
    },
    {
      title: 'Pedidos en curso',
      value: dashboardData.pedidosCurso, // Clave minúscula
      icon: Clock,
      color: 'from-yellow-500 to-orange-600',
      bgColor: 'bg-yellow-100',
      description: 'Pedidos pendientes',
    },
    {
      title: 'Transacciones Realizadas',
      value: dashboardData.transaccionesRealizadas, // Clave minúscula
      icon: ShoppingCart,
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-100',
      description: 'Total de operaciones',
    },
  ];

  if (loading && !applyingFilter) {
    return (
      <div className="container mx-auto px-4 py-8">
        <motion.div
          className="text-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Cargando panel de control...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.h1
        className="text-4xl font-extrabold text-gray-900 mb-8 text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Panel de Control
      </motion.h1>

      {/* Filtro por MES o AÑO con control calendario */}
      <motion.div
        className="mb-8 bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Filtrar por:</label>
          </div>

          {/* Toggle para tipo de filtro */}
          <div className="flex gap-2">
            <button
              onClick={() => handleFilterChange('mes')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'mes'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              MES
            </button>
            <button
              onClick={() => handleFilterChange('año')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'año'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              AÑO
            </button>
          </div>

          {/* Control calendario basado en tipo */}
          {filterType === 'mes' ? (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Seleccionar Mes:</label>
              <input
                type="month"
                value={`${filterValue.ano}-${String(filterValue.mes).padStart(2, '0')}`}
                onChange={handleMonthChange}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Seleccionar Año:</label>
              <input
                type="number"
                value={filterValue.ano}
                onChange={handleYearChange}
                min="2010"
                max={new Date().getFullYear()}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
               )}
        </div>
      </motion.div>

      {/* Mensaje de error con botón de retry si hay problemas */}
      {error && (
        <motion.div
          className="mb-6 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-xl flex items-center gap-2 justify-between"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            {usingFallback && <span className="text-sm font-medium">(Usando datos de ejemplo)</span>}
          </div>
          {!usingFallback && (
            <motion.button
              onClick={handleRetry}
              className="px-3 py-1 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Reintentar
            </motion.button>
          )}
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {indicators.map((indicator, index) => {
          const Icon = indicator.icon;
          return (
            <motion.div
              key={indicator.title}
              className={`bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center text-center ${indicator.bgColor}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <motion.div
                className={`p-3 rounded-full mb-4 ${indicator.color} text-white`}
                whileHover={{ scale: 1.05 }}
              >
                <Icon className="w-6 h-6" />
              </motion.div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{indicator.title}</h3>
              <p className="text-3xl font-bold text-gray-900 mb-1">{indicator.value}</p>
              <p className="text-sm text-gray-600">{indicator.description}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;